import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import {Episode, Season, Series} from "src/app/models/Series";
import {isSeries} from "src/app/utils/works-type-guards.util";
import {WorksService} from "../../../shared/services/works.service";
import {ToastService} from "../../services/toast.service";
import {BaseEditWorkDatesComponent} from "../base-edit-work-dates/base-edit-work-dates.component";
import {PaginatedResponse} from "../../../shared/interfaces/ApiResponse.interface";
import {TranslateService, TranslateModule} from "@ngx-translate/core";
import {WorksTypes} from "src/app/enums/Works.enum";
import {NgClass} from "@angular/common";
import {EditWorkCompletionDatesComponent} from "../edit-work-completion-dates/edit-work-completion-dates.component";
import {UserLocaleService} from "src/app/services/user-locale.service";
import {
  allowsCrossRegionTranslationFallback,
  resolveTranslation,
} from "src/app/utils/translation.util";

@Component({
  selector: "app-edit-series-dates",
  templateUrl: "./edit-series-dates.component.html",
  styleUrls: ["./edit-series-dates.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, EditWorkCompletionDatesComponent, TranslateModule],
})
export class EditSeriesDatesComponent
  extends BaseEditWorkDatesComponent
  implements OnInit, OnChanges
{
  public activeTab?: number = 1;
  public isSeries = isSeries;
  public seriesItem!: Series;

  constructor(
    translate: TranslateService,
    worksService: WorksService,
    toastService: ToastService,
    cdr: ChangeDetectorRef,
    private readonly userLocale: UserLocaleService
  ) {
    super(translate, worksService, toastService, cdr);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.seriesItem = this.item as Series;

    if (this.user && this.seriesItem) {
      const getSeasonsSubscription = this.worksService
        .getWorks(this.user._id, {
          workId: this.item._id,
          filters: JSON.stringify({workType: [WorksTypes.Series]}),
        })
        .subscribe({
          next: (item: PaginatedResponse | null) => {
            if (item) {
              this.seriesItem = {
                ...this.seriesItem,
                seasons: (item.data[0] as Series).seasons?.filter(
                  (season) => season.seasonNumber !== 0
                ),
              };
              this.cdr.markForCheck();
            }
          },
        });
      this.subscriptions.push(getSeasonsSubscription);
    }
  }

  override ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (this.user && changes["item"]?.currentValue) {
      this.seriesItem = changes["item"].currentValue as Series;
      if (this.seriesItem) {
        const getSeasonsSubscription = this.worksService
          .getWorks(this.user._id, {
            workId: this.item._id,
            filters: JSON.stringify({workType: [WorksTypes.Series]}),
          })
          .subscribe({
            next: (item: PaginatedResponse | null) => {
              if (item) {
                this.seriesItem = {
                  ...this.seriesItem,
                  seasons: (item.data[0] as Series).seasons?.filter(
                    (season) => season.seasonNumber !== 0
                  ),
                };
                this.cdr.markForCheck();
              }
            },
          });
        this.subscriptions.push(getSeasonsSubscription);
      }
    }
  }

  public displayTab(event: Event, tab?: number): void {
    this.activeTab = tab ?? undefined;

    // Scroll to link horizontally
    const tabsContainer = document.querySelector(".tabs-container");
    const tabClicked = event.target as HTMLElement;
    const containerRect = tabsContainer?.getBoundingClientRect();
    const tabRect = tabClicked.getBoundingClientRect();

    if (tabsContainer && containerRect) {
      const scrollLeft =
        tabRect.left -
        containerRect.left +
        tabsContainer.scrollLeft -
        (containerRect.width - tabRect.width) / 2;

      tabsContainer.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }

  public getLatestEpisodeViewed():
    | {seasonNumber: number; episodeNumber: number}
    | undefined {
    if (
      !this.seriesItem?.progress?.episodeUpdates ||
      this.seriesItem?.progress?.episodeUpdates?.length === 0
    ) {
      return undefined;
    }

    let maxSeasonNumber = 1;
    let maxEpisodeNumber = 1;
    const progress = this.seriesItem?.progress;
    if (progress?.episodeUpdates) {
      maxSeasonNumber = Math.max(
        ...progress.episodeUpdates.map((update) => update.seasonNumber)
      );

      const episodesOfLastSeason = progress.episodeUpdates.filter(
        (update) => update.seasonNumber === maxSeasonNumber
      );
      maxEpisodeNumber = Math.max(
        ...episodesOfLastSeason.map((update) => update.episodeNumber)
      );
    }

    return {
      seasonNumber: maxSeasonNumber,
      episodeNumber: maxEpisodeNumber,
    };
  }

  onKeyUp(event: KeyboardEvent, type: string, season?: number): void {
    if (event.key === "Enter" || event.key === " ") {
      if (type === "displayTab") {
        this.displayTab(event, season);
      }
    }
  }

  public trackBySeason(_index: number, item: Season): number {
    return item.seasonNumber;
  }

  public trackByEpisode(_index: number, item: Episode): number {
    return item.number;
  }

  public getEpisodeTranslation(item: Episode, field: string): string {
    const locale = this.userLocale.getLocale();
    if (locale.split("-")[0] !== "en") {
      const translated = resolveTranslation(item.translations, field, locale, {
        crossRegionFallback: allowsCrossRegionTranslationFallback(field),
      });
      if (translated) {
        return translated;
      }
    }

    return item[field as keyof Episode] as string;
  }
}
